import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CheckOutlined,
  CloseOutlined,
  CoffeeOutlined,
  IdcardOutlined,
  LinkOutlined,
  SaveOutlined,
  UserSwitchOutlined,
  FolderOpenOutlined,
  DeleteOutlined,
  EditOutlined,
  MedicineBoxOutlined,
  PlusOutlined,
  SmileOutlined
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Tabs,
  Tag,
  TimePicker,
  Tooltip,
  message
} from "antd";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import {
  getSetting,
  getWorkplaceNumber,
  loadRuntimeSettings
} from "@core/runtime-settings";
import ClientDailyMealsModal from "@components/nutrition/client-daily-meals-modal";
import DocExpClientCardDrawer from "./doc-exp-client-card-drawer";
import ClientDrawer from "@components/health/clients/client-drawer";
import { createSprPred, fetchSprPred, fetchSprPredList, matchSprPredPhones } from "../../sprs/pred/spr-pred-api";
import SprPred from "../../sprs/pred/spr-pred";
import PhoneInput, { emailRule, phoneRule } from "../../sprs/pred/phone-input";
import { createDocExp, fetchDocExp, fetchDocExpLookups, fetchDocExpRecalculateCheck, saveDocExp } from "./doc-exp-api";
import DocExpCntsPanel from "./doc-exp-cnts-panel";
import DocExpPayDrawer from "./doc-exp-pay-drawer";
import DocExpPaymentsPanel from "./doc-exp-payments-panel";
import DocExpSendToDoctorModal from "./doc-exp-send-to-doctor-modal";
import DocExpPlacementsPanel from "./doc-exp-placements-panel";
import mealBreakfastSrc from "./assets/meal-breakfast.svg";
import mealDinnerSrc from "./assets/meal-dinner.svg";
import mealLunchSrc from "./assets/meal-lunch.svg";
import mealSnackSrc from "./assets/meal-snack.svg";
import {
  CNT_IS_AUTO,
  SEX_OPTIONS,
  VID_MESTA_OPTIONS,
  emptyCard,
  emptyClient,
  phoneKey,
  formatMoney,
  getLawFlag,
  mealFlagsFromRazmer,
  normalizeCard,
  normalizeLookups,
  pick,
  seatsErrorText,
  seatsSummary,
  toApiDate,
  toApiDateTime,
  toNumber
} from "./doc-exp-utils";

const LABEL_WIDTH = 140;
const DEFAULT_TIME_BEG = "14:00";
const DEFAULT_TIME_END = "12:00";

const formItemLayout = {
  layout: "horizontal",
  labelCol: { flex: `0 0 ${LABEL_WIDTH}px` },
  wrapperCol: { flex: "1 1 auto", minWidth: 0, maxWidth: "100%" },
  labelAlign: "right"
};

const toDay = (value) => {
  if (!value) return null;
  // Date-only / midnight values: keep calendar day (no UTC shift).
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const d = dayjs(value.slice(0, 10), "YYYY-MM-DD");
    return d.isValid() ? d : null;
  }
  const d = dayjs(value);
  return d.isValid() ? d.startOf("day") : null;
};

const toDateTime = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

const toClock = (value, fallback) => {
  const d = toDateTime(value);
  if (d) return d;
  return fallback ? dayjs(fallback, "HH:mm") : null;
};

const mergeDateTime = (date, time) => {
  if (!date) return null;
  const next = date.startOf("day");
  if (!time) return next;
  return next.hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
};

const mapPredOptions = (data) => {
  const rows = Array.isArray(data) ? data : pick(data, "items", "Items") || [];
  return rows
    .map((row) => {
      const value = toNumber(pick(row, "idPred", "IdPred"));
      if (!value) return null;
      return { value, label: pick(row, "name", "Name") || String(value) };
    })
    .filter(Boolean);
};

const mergePredOptions = (previous, next) => {
  const map = new Map([...previous, ...next].map((item) => [item.value, item]));
  return [...map.values()];
};

const requiredMark = (label, { required }) => (
  <>
    {required ? <span className="doc-exp-card__star">*</span> : null}
    {label}
  </>
);

const tabLabel = (text, count) => (
  <span className="doc-exp-card__tab-label">
    {text}
    {count != null ? <span className="doc-exp-card__tab-badge">{count}</span> : null}
  </span>
);

const MealCheck = ({ name, title, disabled, src }) => (
  <Tooltip title={title}>
    <span className="doc-exp-card__meal-wrap">
      <Form.Item name={name} valuePropName="checked" noStyle>
        <Checkbox disabled={disabled} aria-label={title}>
          <span className="doc-exp-card__meal-icon" style={{ "--meal-icon": `url("${src}")` }} />
        </Checkbox>
      </Form.Item>
    </span>
  </Tooltip>
);

const mapClientsForForm = (clients) =>
  (clients || []).map((c) => ({
    ...c,
    dateBird: toDay(c.dateBird),
    dateBeg: toDateTime(c.dateBeg),
    dateEnd: toDateTime(c.dateEnd)
  }));

const mapClientsForApi = (clients) =>
  (clients || []).map((c) => ({
    idStr: c.idStr || null,
    name: (c.name || "").trim(),
    dateBird: toApiDate(c.dateBird),
    sex: c.sex ?? 0,
    isBaby: c.isBaby ? 1 : 0,
    phone: c.phone || "",
    email: c.email || "",
    pasport: c.pasport || "",
    adres: c.adres || "",
    komment: c.komment || "",
    docnum: c.docnum || 0,
    vidMesta: c.vidMesta || 0,
    vidLech: c.vidLech || 0,
    workPlace: c.workPlace || "",
    workPosition: c.workPosition || "",
    smsAgree: c.smsAgree ? 1 : 0,
    skidkaAcc: c.skidkaAcc ?? 0,
    skidkaPit: c.skidkaPit ?? 0,
    skidkaLech: c.skidkaLech ?? 0,
    idNumber: c.idNumber || 0,
    idNumberCat: c.idNumberCat || 0,
    dateBeg: toApiDateTime(c.dateBeg),
    dateEnd: toApiDateTime(c.dateEnd),
    idPred: c.idPred || 0
  }));

const mapCntsForForm = (cnts) =>
  (cnts || []).map((c) => ({
    ...c,
    idTov: c.idTov || undefined,
    fdate: toDay(c.fdate),
    isAuto: c.isAuto ?? CNT_IS_AUTO.EXTRA
  }));

/** На сервер уходят только ручные строки (IS_AUTO = 0). */
const mapCntsForApi = (cnts) =>
  (cnts || [])
    .filter((c) => (Number(c.isAuto) || 0) === CNT_IS_AUTO.EXTRA)
    .map((c) => ({
      idStr: c.idStr || null,
      idTov: c.idTov || 0,
      zenaExp: c.zenaExp ?? 0,
      kol: c.kol ?? 0,
      prcDiscount: c.prcDiscount ?? 0,
      symaDiscount: c.symaDiscount ?? 0,
      fdate: toApiDate(c.fdate),
      komment: c.komment || "",
      idSkl: c.idSkl || 0
    }));

const cloneDay = (value) =>
  value && typeof value.clone === "function" ? value.clone() : value;

const snapshotClientValues = (client) => {
  if (!client) return emptyClient();
  return {
    ...client,
    dateBird: cloneDay(client.dateBird),
    dateBeg: cloneDay(client.dateBeg),
    dateEnd: cloneDay(client.dateEnd)
  };
};

const sexLetter = (sex) => (Number(sex) === 1 ? "М" : "Ж");

const vidMestaTag = (vidMesta) => {
  const value = Number(vidMesta) || 0;
  const label = VID_MESTA_OPTIONS.find((o) => o.value === value)?.label || "Основное";
  return {
    label,
    color: value === 1 ? "orange" : "blue"
  };
};

/** Поля гостя, которые берутся из карточки контрагента (Delphi SetVisibledControls). */
const clientFromPred = (pred) => ({
  name: pred?.name || "",
  phone: pred?.phone || "",
  email: pred?.email || "",
  adres: pred?.adress || "",
  pasport: pred?.pasport || "",
  dateBird: pred?.dateBird ? dayjs(pred.dateBird) : null,
  sex: Number(pred?.sex) || 0,
  workPlace: pred?.workPlace || "",
  workPosition: pred?.workPosition || "",
  smsAgree: Number(pred?.smsAgree) === 1
});

const firstVisitText = (c, fmt = "DD.MM.YYYY HH:mm") =>
  c?.firstVisit
    ? [dayjs(c.firstVisit).format(fmt), c.firstVisitUsl, c.firstVisitZal].filter(Boolean).join(" · ")
    : "";

const doctorInfoText = (c) => {
  if (!c?.dateVizit) return "";
  const parts = [`Отправлен к врачу${c.sotrName ? ` ${c.sotrName}` : ""} ${dayjs(c.dateVizit).format("DD.MM.YYYY HH:mm")}`];
  if (c.firstVisit) parts.push(`первый визит ${firstVisitText(c)}`);
  return parts.join(", ");
};

const predFromClient = (c) => ({
  vid: 0,
  name: (c.name || "").trim(),
  phone: c.phone || "",
  email: c.email || "",
  adress: c.adres || "",
  pasport: c.pasport || "",
  dateBird: toApiDate(c.dateBird),
  sex: c.sex ?? 0,
  workPlace: c.workPlace || "",
  workPosition: c.workPosition || "",
  smsAgree: c.smsAgree ? 1 : 0
});

const ClientSummary = ({ client, index, predMatch, onLinkPred, onOpenPred }) => {
  const c = client || {};
  const name = (c.name || "").trim() || `Клиент ${index + 1}`;
  const contacts = [c.phone, c.adres, c.email].map((v) => (v || "").trim()).filter(Boolean);
  const comment = (c.komment || "").trim();
  const mesta = vidMestaTag(c.vidMesta);

  return (
    <div className="doc-exp-card__client-summary">
      <div className="doc-exp-card__client-summary-row doc-exp-card__client-summary-row--main">
        <Tag className="doc-exp-card__client-summary-mesta" color={mesta.color}>
          {mesta.label}
        </Tag>
        <span className="doc-exp-card__client-summary-name">{name}</span>
        <span className="doc-exp-card__client-summary-sex" title={sexLetter(c.sex) === "М" ? "Мужской" : "Женский"}>
          {sexLetter(c.sex)}
        </span>
        {c.isBaby ? (
          <Tooltip title="Ребёнок">
            <SmileOutlined className="doc-exp-card__client-summary-baby" aria-label="Ребёнок" />
          </Tooltip>
        ) : null}
        {c.keyCardShort ? (
          <Tag className="doc-exp-card__client-summary-card" color="green">
            {`Карта ${c.keyCardShort}`}
          </Tag>
        ) : null}
        {c.dateVizit ? (
          <Tooltip title={doctorInfoText(c)}>
            <MedicineBoxOutlined className="doc-exp-card__client-summary-doctor" />
          </Tooltip>
        ) : null}
        {c.firstVisit ? (
          <span className="doc-exp-card__client-summary-visit">визит {firstVisitText(c, "DD.MM HH:mm")}</span>
        ) : null}
      </div>
      {contacts.length > 0 || c.idPred || predMatch ? (
        <div className="doc-exp-card__client-summary-row doc-exp-card__client-summary-row--contacts">
          {contacts.length > 0 ? <span className="doc-exp-card__client-summary-contacts">{contacts.join(" · ")}</span> : null}
          {c.idPred ? (
            <Tooltip title={onOpenPred ? "Открыть карточку контрагента" : `Гость привязан к контрагенту №${c.idPred}`}>
              <Tag
                color="geekblue"
                icon={<LinkOutlined />}
                className={`doc-exp-card__client-summary-pred${onOpenPred ? " doc-exp-card__client-summary-pred--link" : ""}`}
                onClick={onOpenPred ? (e) => { e.stopPropagation(); onOpenPred(Number(c.idPred)); } : undefined}
              >
                {c.predName || `Контрагент №${c.idPred}`}
              </Tag>
            </Tooltip>
          ) : predMatch ? (
            <Tooltip title={`В справочнике есть контрагент с таким телефоном: ${predMatch.name}. Нажмите, чтобы привязать.`}>
              <Tag
                color="gold"
                className="doc-exp-card__client-summary-pred doc-exp-card__client-summary-pred--match"
                onClick={(e) => {
                  e.stopPropagation();
                  onLinkPred?.(predMatch.idPred);
                }}
              >
                Есть в справочнике
              </Tag>
            </Tooltip>
          ) : null}
        </div>
      ) : null}
      {comment ? (
        <div className="doc-exp-card__client-summary-row doc-exp-card__client-summary-row--comment">
          {comment}
        </div>
      ) : null}
    </div>
  );
};

/** Данные привязанного контрагента только для чтения; правка — через карточку контрагента. */
const PredInfo = ({ client, readOnly, onOpen }) => {
  const c = client || {};
  const rows = [
    ["ФИО", c.name],
    ["Дата рождения", c.dateBird ? dayjs(c.dateBird).format("DD.MM.YYYY") : ""],
    ["Пол", c.sex === 1 ? "Мужской" : c.sex === 0 || c.sex === "0" ? "Женский" : ""],
    ["Телефон", c.phone],
    ["E-mail", c.email],
    ["Паспорт", c.pasport],
    ["Адрес", c.adres],
    ["Место работы", [c.workPlace, c.workPosition].filter(Boolean).join(", ")],
    ["Согласие SMS", c.smsAgree ? "да" : ""]
  ].filter(([, v]) => String(v || "").trim());
  return (
    <div className="doc-exp-card__pred-info">
      <div className="doc-exp-card__pred-info-head">
        <span>Данные из справочника контрагентов</span>
        {!readOnly ? (
          <Button type="link" size="small" icon={<EditOutlined />} onClick={onOpen}>
            Изменить в карточке
          </Button>
        ) : null}
      </div>
      <dl className="doc-exp-card__pred-info-grid">
        {rows.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
};

/**
 * Привязка гостя к контрагенту. Delphi: TFrmEDocExpClient (BitBtn3/BtnPredGetFromDoc/BtnSaveToSpr/BtnPredClear).
 */
const ClientPredField = ({ field, client, readOnly, formItemLayout, predMatch, applyPred, onOpenPred }) => {
  const form = Form.useFormInstance();
  const headerIdClient = Form.useWatch("idClient", form);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(0);
  const idPred = Number(client?.idPred) || 0;

  const search = (q) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setLoading(true);
      fetchSprPredList({ q, take: 30 })
        .then((data) => setOptions(mapPredOptions(data)))
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    }, 250);
  };
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const unlink = () => {
    form.setFieldValue(["clients", field.name, "idPred"], 0);
    form.setFieldValue(["clients", field.name, "predName"], "");
  };

  const saveToSpr = async () => {
    const c = form.getFieldValue(["clients", field.name]) || {};
    if (!(c.name || "").trim()) {
      message.warning("Укажите ФИО гостя");
      return;
    }
    setBusy(true);
    try {
      if (phoneKey(c.phone).length >= 5) {
        const matches = await matchSprPredPhones([c.phone]);
        const found = Array.isArray(matches) ? matches[0] : null;
        if (found) {
          Modal.confirm({
            title: "Телефон уже есть в справочнике",
            content: `Контрагент «${found.name}» имеет такой же телефон. Привязать гостя к нему вместо создания нового?`,
            okText: "Привязать",
            cancelText: "Отмена",
            onOk: () => applyPred(field.name, found.idPred)
          });
          return;
        }
      }
      const saved = await createSprPred(predFromClient(c));
      const id = toNumber(pick(saved, "idPred", "IdPred"));
      if (id) {
        form.setFieldValue(["clients", field.name, "idPred"], id);
        form.setFieldValue(["clients", field.name, "predName"], pick(saved, "name", "Name") || c.name);
        message.success("Контрагент сохранён в справочник");
      }
    } catch (e) {
      message.error(e.message || "Не удалось сохранить в справочник");
    } finally {
      setBusy(false);
    }
  };

  const selectOptions = idPred && !options.some((o) => o.value === idPred)
    ? [{ value: idPred, label: client.predName || `Контрагент №${idPred}` }, ...options]
    : options;

  return (
    <>
      <Form.Item {...formItemLayout} label="Контрагент">
        <div className="doc-exp-card__pred-row">
          <Select
            className="doc-exp-card__pred-select"
            showSearch
            allowClear
            value={idPred || undefined}
            options={selectOptions}
            filterOption={false}
            onSearch={search}
            onOpenChange={(opened) => opened && search("")}
            loading={loading}
            disabled={readOnly || busy}
            placeholder="Не привязан — выберите из справочника"
            notFoundContent={loading ? "Загрузка…" : "Не найдено"}
            onChange={(value) => (value ? applyPred(field.name, value) : unlink())}
          />
          {!readOnly ? (
            <Space size={0}>
              {idPred ? (
                <Tooltip title="Открыть карточку контрагента">
                  <Button type="text" size="small" icon={<FolderOpenOutlined />} aria-label="Открыть контрагента"
                    onClick={() => onOpenPred(idPred)} />
                </Tooltip>
              ) : (
                <Tooltip title="Сохранить гостя в справочник контрагентов">
                  <Button type="text" size="small" icon={<SaveOutlined />} aria-label="Сохранить в справочник"
                    loading={busy} onClick={saveToSpr} />
                </Tooltip>
              )}
              {!idPred && headerIdClient ? (
                <Tooltip title="Привязать контрагента из путёвки">
                  <Button type="text" size="small" icon={<UserSwitchOutlined />} aria-label="Из путёвки"
                    onClick={() => applyPred(field.name, headerIdClient)} />
                </Tooltip>
              ) : null}
            </Space>
          ) : null}
        </div>
      </Form.Item>
      {!idPred && predMatch ? (
        <Alert
          type="warning"
          showIcon
          className="doc-exp-card__pred-match"
          message={<>
            В справочнике есть контрагент с таким телефоном: <b>{predMatch.name}</b>
            {predMatch.dateBird ? ` (${dayjs(predMatch.dateBird).format("DD.MM.YYYY")})` : ""}
          </>}
          action={!readOnly ? (
            <Button size="small" type="primary" onClick={() => applyPred(field.name, predMatch.idPred)}>
              Привязать
            </Button>
          ) : null}
        />
      ) : null}
    </>
  );
};

/**
 * Список клиентов: сокращённый блок по умолчанию, форма по «Изменить».
 */
const DocExpClientsFields = ({
  fields,
  add,
  remove,
  clientsWatch,
  clientsReadOnly,
  formItemLayout,
  lookups,
  editingKey,
  setEditingKey,
  expandLastRef,
  saving,
  onConfirmEdit,
  defaultVidLech,
  idDoc,
  canEditDailyMeals,
  mealTypeOptions,
  onDailyMealsSaved,
  canSendToDoctor,
  onSendToDoctor,
  seats,
  numberName,
  catName,
  onDeleteClient,
  onReload
}) => {
  const [dailyMealsClient, setDailyMealsClient] = useState(null);
  const [cardClient, setCardClient] = useState(null);
  const [healthClientId, setHealthClientId] = useState(null);
  const [predEditId, setPredEditId] = useState(null);
  const [phoneMatches, setPhoneMatches] = useState({});
  const form = Form.useFormInstance();
  const editSnapshotRef = useRef(null);

  const phonesKey = JSON.stringify(
    (clientsWatch || [])
      .filter((c) => !(Number(c?.idPred) > 0) && phoneKey(c?.phone).length >= 5)
      .map((c) => c.phone)
  );
  useEffect(() => {
    const phones = JSON.parse(phonesKey);
    if (!phones.length) {
      setPhoneMatches({});
      return undefined;
    }
    const ac = new AbortController();
    const t = setTimeout(() => {
      matchSprPredPhones(phones, ac.signal)
        .then((rows) => {
          if (ac.signal.aborted) return;
          const map = {};
          (Array.isArray(rows) ? rows : []).forEach((m) => {
            const key = phoneKey(m.phone);
            if (!map[key]) map[key] = m;
          });
          setPhoneMatches(map);
        })
        .catch(() => {});
    }, 500);
    return () => {
      clearTimeout(t);
      ac.abort();
    };
  }, [phonesKey]);

  const matchFor = (client) =>
    !(Number(client?.idPred) > 0) ? phoneMatches[phoneKey(client?.phone)] || null : null;

  const applyPred = async (fieldName, idPred) => {
    try {
      const pred = await fetchSprPred(idPred);
      const current = form.getFieldValue(["clients", fieldName]) || {};
      form.setFieldValue(["clients", fieldName], {
        ...current,
        ...clientFromPred(pred),
        idPred,
        predName: pred?.name || ""
      });
    } catch (e) {
      message.error(e.message || "Не удалось загрузить контрагента");
    }
  };

  const beginEdit = (field) => {
    const values = form.getFieldValue(["clients", field.name]);
    editSnapshotRef.current = {
      key: field.key,
      name: field.name,
      values: snapshotClientValues(values),
      isNew: !(values?.idStr > 0)
    };
    setEditingKey(field.key);
  };

  const cancelEdit = (field) => {
    const snap = editSnapshotRef.current;
    if (snap && snap.key === field.key) {
      if (snap.isNew) {
        remove(field.name);
      } else {
        form.setFieldValue(["clients", field.name], snapshotClientValues(snap.values));
      }
    }
    editSnapshotRef.current = null;
    setEditingKey(null);
  };

  useEffect(() => {
    if (!expandLastRef.current || fields.length === 0) return;
    expandLastRef.current = false;
    beginEdit(fields[fields.length - 1]);
  }, [fields, expandLastRef, setEditingKey]);

  const seatsSum = seatsSummary(clientsWatch, seats);

  return (
    <div className="doc-exp-card__clients">
      {seats ? (
        <Alert
          className="doc-exp-card__seats"
          type={seatsSum.overOsn || seatsSum.overDop ? "error" : "info"}
          showIcon
          message={
            <span className="doc-exp-card__seats-line">
              <span className="doc-exp-card__seats-title">{seats.source === "category" ? `Категория ${catName}` : `Номер ${numberName}`}</span>
              <span className={`doc-exp-card__seats-item${seatsSum.overOsn ? " is-over" : ""}`}>основных {seatsSum.usedOsn} / {seatsSum.availOsn}</span>
              <span className={`doc-exp-card__seats-item${seatsSum.overDop ? " is-over" : ""}`}>дополнительных {seatsSum.usedDop} / {seatsSum.availDop}</span>
              {seats.otherOsn + seats.otherDop > 0 ? <span className="doc-exp-card__seats-other">занято другими путёвками: {seats.otherOsn} осн. / {seats.otherDop} доп.</span> : null}
            </span>
          }
          description={seatsErrorText(seatsSum) || undefined}
        />
      ) : null}
      {fields.length === 0 ? (
        <div className="doc-exp-card__clients-empty">Нет клиентов</div>
      ) : null}
      {fields.map((field, index) => {
        const client = clientsWatch[index] || {};
        const editing = editingKey === field.key;
        const title = (client.name || "").trim() || `Клиент ${index + 1}`;
        const linked = Number(client.idPred) > 0;

        return (
          <div
            className={`doc-exp-card__client-block${
              editing ? " doc-exp-card__client-block--open" : " doc-exp-card__client-block--clickable"
            }`}
            key={field.key}
            onClick={editing || saving ? undefined : () => beginEdit(field)}
          >
            <div className="doc-exp-card__client-block-head">
              <div className="doc-exp-card__client-block-summary">
                {editing ? (
                  <span className="doc-exp-card__client-block-title">{title}</span>
                ) : (
                  <ClientSummary
                    client={client}
                    index={index}
                    predMatch={matchFor(client)}
                    onOpenPred={saving ? undefined : setPredEditId}
                    onLinkPred={clientsReadOnly || saving ? undefined : (id) => {
                      beginEdit(field);
                      applyPred(field.name, id);
                    }}
                  />
                )}
              </div>
              <Space
                size={0}
                className="doc-exp-card__client-block-actions"
                onClick={(event) => event.stopPropagation()}
              >
                {idDoc && client.idStr ? (
                  <Tooltip title="Питание по дням">
                    <Button
                      type="text"
                      size="small"
                      icon={<CoffeeOutlined />}
                      disabled={saving}
                      onClick={() => setDailyMealsClient(client)}
                      aria-label="Питание по дням"
                    />
                  </Tooltip>
                ) : null}
                {idDoc && client.idStr ? (
                  <Tooltip title="Карта гостя">
                    <Button
                      type="text"
                      size="small"
                      icon={<IdcardOutlined />}
                      disabled={saving}
                      onClick={() => setCardClient(client)}
                      aria-label="Карта гостя"
                    />
                  </Tooltip>
                ) : null}
                {idDoc && client.idStr ? (
                  <Tooltip
                    title={
                      client.dateVizit
                        ? `${doctorInfoText(client)}. Открыть карточку клиента`
                        : canSendToDoctor
                          ? "К врачу"
                          : "Отправлять к врачу можно клиента только заселенной путевки"
                    }
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={<MedicineBoxOutlined />}
                      className={client.dateVizit ? "doc-exp-card__client-doctor-sent" : undefined}
                      disabled={saving || (!client.dateVizit && !canSendToDoctor)}
                      onClick={() =>
                        client.dateVizit
                          ? Number(client.idClient) > 0
                            ? setHealthClientId(Number(client.idClient))
                            : message.error("Карточка клиента лечебной части не найдена")
                          : onSendToDoctor?.(client)
                      }
                      aria-label="К врачу"
                    />
                  </Tooltip>
                ) : null}
                {editing ? (
                  <>
                    <Tooltip title={clientsReadOnly ? "Свернуть" : "Отменить"}>
                      <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        disabled={saving}
                        onClick={() => cancelEdit(field)}
                        aria-label={clientsReadOnly ? "Свернуть" : "Отменить"}
                      />
                    </Tooltip>
                    {!clientsReadOnly ? (
                      <Tooltip title="Подтвердить">
                        <Button
                          type="text"
                          size="small"
                          icon={<CheckOutlined />}
                          loading={saving}
                          disabled={saving}
                          onClick={async () => {
                            const ok = await onConfirmEdit?.(field.name);
                            if (ok) {
                              editSnapshotRef.current = null;
                              setEditingKey(null);
                            }
                          }}
                          aria-label="Подтвердить"
                        />
                      </Tooltip>
                    ) : null}
                  </>
                ) : (
                  <Tooltip title={clientsReadOnly ? "Просмотр" : "Изменить"}>
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      disabled={saving}
                      onClick={() => beginEdit(field)}
                      aria-label={clientsReadOnly ? "Просмотр" : "Изменить"}
                    />
                  </Tooltip>
                )}
                {!clientsReadOnly ? (
                  <Tooltip title="Удалить">
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      disabled={saving}
                      onClick={() => {
                        const name = (client.name || "").trim() || "Гость без имени";
                        const persisted = Number(client.idStr) > 0 && idDoc != null;
                        Modal.confirm({
                          title: "Удалить гостя?",
                          content: persisted
                            ? `${name}. Гость будет удалён из путёвки, размещение и стоимость будут пересчитаны.`
                            : `${name}.`,
                          okText: "Удалить",
                          okButtonProps: { danger: true },
                          cancelText: "Отмена",
                          onOk: async () => {
                            if (editingKey === field.key) {
                              editSnapshotRef.current = null;
                              setEditingKey(null);
                            }
                            if (!persisted) {
                              remove(field.name);
                              return;
                            }
                            await onDeleteClient?.(field.name);
                          }
                        });
                      }}
                      aria-label="Удалить клиента"
                    />
                  </Tooltip>
                ) : null}
              </Space>
            </div>

            {/* Поля всегда в DOM: иначе Form.List/useWatch теряют значения в свёрнутом виде. */}
            <div
              className="doc-exp-card__client-block-body"
              hidden={!editing}
              aria-hidden={!editing}
            >
              <Form.Item name={[field.name, "idStr"]} hidden>
                <InputNumber />
              </Form.Item>
              <Form.Item name={[field.name, "numberName"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={[field.name, "catName"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={[field.name, "idPred"]} hidden>
                <InputNumber />
              </Form.Item>
              <Form.Item name={[field.name, "predName"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={[field.name, "dateVizit"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={[field.name, "sotrName"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={[field.name, "firstVisit"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={[field.name, "firstVisitZal"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={[field.name, "firstVisitUsl"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={[field.name, "idClient"]} hidden>
                <InputNumber />
              </Form.Item>
              {editing ? (
                <ClientPredField
                  field={field}
                  client={client}
                  readOnly={clientsReadOnly}
                  formItemLayout={formItemLayout}
                  predMatch={matchFor(client)}
                  applyPred={applyPred}
                  onOpenPred={setPredEditId}
                />
              ) : null}
              {linked ? (
                <PredInfo client={client} readOnly={clientsReadOnly} onOpen={() => setPredEditId(client.idPred)} />
              ) : null}
              <div hidden={linked} aria-hidden={linked}>
                <Form.Item {...formItemLayout} label="ФИО" name={[field.name, "name"]}>
                  <Input disabled={clientsReadOnly} placeholder="Необязательно" />
                </Form.Item>
                <Form.Item {...formItemLayout} label="Дата рождения" name={[field.name, "dateBird"]}>
                  <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} disabled={clientsReadOnly} />
                </Form.Item>
                <Form.Item {...formItemLayout} label="Пол" name={[field.name, "sex"]}>
                  <Select options={SEX_OPTIONS} disabled={clientsReadOnly} />
                </Form.Item>
                <Form.Item {...formItemLayout} label="Телефон" name={[field.name, "phone"]} rules={[phoneRule]} validateTrigger="onBlur">
                  <PhoneInput disabled={clientsReadOnly} />
                </Form.Item>
                <Form.Item {...formItemLayout} label="E-mail" name={[field.name, "email"]} rules={[emailRule]} validateTrigger="onBlur">
                  <Input disabled={clientsReadOnly} inputMode="email" />
                </Form.Item>
                <Form.Item {...formItemLayout} label="Паспорт" name={[field.name, "pasport"]}>
                  <Input disabled={clientsReadOnly} />
                </Form.Item>
                <Form.Item {...formItemLayout} label="Адрес" name={[field.name, "adres"]}>
                  <Input disabled={clientsReadOnly} />
                </Form.Item>
                <Form.Item {...formItemLayout} label="Место работы" name={[field.name, "workPlace"]}>
                  <Input disabled={clientsReadOnly} />
                </Form.Item>
                <Form.Item {...formItemLayout} label="Должность" name={[field.name, "workPosition"]}>
                  <Input disabled={clientsReadOnly} />
                </Form.Item>
                <Form.Item {...formItemLayout} label="Согласие SMS" name={[field.name, "smsAgree"]} valuePropName="checked">
                  <Checkbox disabled={clientsReadOnly} />
                </Form.Item>
              </div>
              <div className="doc-exp-card__client-stay">
                <Form.Item label="Вид места" name={[field.name, "vidMesta"]}>
                  <Select options={VID_MESTA_OPTIONS} disabled={clientsReadOnly} />
                </Form.Item>
                <Form.Item label="Вид лечения" name={[field.name, "vidLech"]}>
                  <Select options={lookups.vidLech} disabled={clientsReadOnly} showSearch optionFilterProp="label" />
                </Form.Item>
                <Form.Item label="№ путёвки" name={[field.name, "docnum"]}>
                  <InputNumber min={0} style={{ width: "100%" }} disabled={clientsReadOnly} />
                </Form.Item>
                <Form.Item name={[field.name, "isBaby"]} valuePropName="checked" className="doc-exp-card__client-stay-check">
                  <Checkbox disabled={clientsReadOnly}>Ребёнок</Checkbox>
                </Form.Item>
                <Form.Item label="Комментарий" name={[field.name, "komment"]} className="doc-exp-card__client-stay-wide">
                  <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} disabled={clientsReadOnly} />
                </Form.Item>
                <Form.Item name={[field.name, "skidkaAcc"]} hidden><InputNumber /></Form.Item>
                <Form.Item name={[field.name, "skidkaPit"]} hidden><InputNumber /></Form.Item>
                <Form.Item name={[field.name, "skidkaLech"]} hidden><InputNumber /></Form.Item>
              </div>
              {(client.numberName || client.catName) && (
                <Form.Item {...formItemLayout} label="Номер / кат.">
                  <Input
                    disabled
                    value={[client.numberName, client.catName].filter(Boolean).join(" / ")}
                  />
                </Form.Item>
              )}
              {client.dateVizit ? (
                <Form.Item {...formItemLayout} label="Врач">
                  <Input
                    disabled
                    value={[client.sotrName, dayjs(client.dateVizit).format("DD.MM.YYYY HH:mm")].filter(Boolean).join(" · ")}
                  />
                </Form.Item>
              ) : null}
              {client.firstVisit ? (
                <Form.Item {...formItemLayout} label="Первый визит">
                  <Input disabled value={firstVisitText(client)} />
                </Form.Item>
              ) : null}
            </div>
          </div>
        );
      })}
      {!clientsReadOnly ? (
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => {
            expandLastRef.current = true;
            add({ ...emptyClient(), vidLech: defaultVidLech });
          }}
          block
          className="doc-exp-card__client-add"
        >
          Добавить клиента
        </Button>
      ) : null}
      <ClientDailyMealsModal
        open={Boolean(dailyMealsClient)}
        idDoc={idDoc}
        idStr={dailyMealsClient?.idStr}
        clientName={dailyMealsClient?.name}
        mealTypeOptions={mealTypeOptions}
        canEdit={canEditDailyMeals}
        onClose={() => setDailyMealsClient(null)}
        onSaved={onDailyMealsSaved}
      />
      <DocExpClientCardDrawer
        open={Boolean(cardClient)}
        idDoc={idDoc}
        idStr={cardClient?.idStr}
        clientName={cardClient?.name}
        onClose={() => setCardClient(null)}
        onSaved={onDailyMealsSaved}
      />
      <SprPred
        open={predEditId != null}
        idPred={predEditId}
        onClose={() => setPredEditId(null)}
        onSaved={async (saved) => {
          const id = toNumber(pick(saved, "idPred", "IdPred")) || predEditId;
          const idx = (clientsWatch || []).findIndex((c) => Number(c?.idPred) === id);
          if (idx >= 0) {
            await applyPred(idx, id);
            if (!clientsReadOnly && Number(clientsWatch[idx]?.idStr) > 0) {
              await onConfirmEdit?.(idx);
            }
          }
          setPredEditId(null);
        }}
      />
      <ClientDrawer
        open={healthClientId != null}
        idClient={healthClientId}
        onClose={() => setHealthClientId(null)}
        onSaved={() => setHealthClientId(null)}
        onDeleted={() => {
          setHealthClientId(null);
          onReload?.();
        }}
      />
    </div>
  );
};

const DocExpClientsPanel = ({
  clientsWatch,
  clientsReadOnly,
  formItemLayout,
  lookups,
  resetKey,
  saving,
  onConfirmEdit,
  defaultVidLech,
  idDoc,
  canEditDailyMeals,
  mealTypeOptions,
  onDailyMealsSaved,
  canSendToDoctor,
  onSendToDoctor,
  seats,
  numberName,
  catName,
  onDeleteClient,
  onReload
}) => {
  const [editingKey, setEditingKey] = useState(null);
  const expandLastRef = useRef(false);

  useEffect(() => {
    setEditingKey(null);
    expandLastRef.current = false;
  }, [resetKey]);

  return (
    <Form.List name="clients">
      {(fields, { add, remove }) => (
        <DocExpClientsFields
          fields={fields}
          add={add}
          remove={remove}
          clientsWatch={clientsWatch}
          clientsReadOnly={clientsReadOnly}
          formItemLayout={formItemLayout}
          lookups={lookups}
          editingKey={editingKey}
          setEditingKey={setEditingKey}
          expandLastRef={expandLastRef}
          saving={saving}
          onConfirmEdit={onConfirmEdit}
          defaultVidLech={defaultVidLech}
          idDoc={idDoc}
          canEditDailyMeals={canEditDailyMeals}
          mealTypeOptions={mealTypeOptions}
          onDailyMealsSaved={onDailyMealsSaved}
          canSendToDoctor={canSendToDoctor}
          onSendToDoctor={onSendToDoctor}
          seats={seats}
          numberName={numberName}
          catName={catName}
          onDeleteClient={onDeleteClient}
          onReload={onReload}
        />
      )}
    </Form.List>
  );
};

const toLifecycleMeta = (card) =>
  card
    ? {
        idDoc: card.idDoc,
        docnum: card.docnum,
        idStatus: card.idStatus,
        isClosed: card.isClosed,
        dateBeg: card.dateBeg,
        dateEnd: card.dateEnd,
        numberName: card.numberName || "",
        catName: card.catName || "",
        canRecalculate: card.canRecalculate !== false,
        recalculateBlockReason: card.recalculateBlockReason || ""
      }
    : null;

const applyCardToForm = (form, card) => {
  form.setFieldsValue({
    ...card,
    docdate: toDay(card.docdate),
    dateBeg: toDateTime(card.dateBeg) || toDay(card.dateBeg),
    dateEnd: toDateTime(card.dateEnd) || toDay(card.dateEnd),
    timeBeg: toClock(card.dateBeg, DEFAULT_TIME_BEG),
    timeEnd: toClock(card.dateEnd, DEFAULT_TIME_END),
    dateBron: toDay(card.dateBron),
    clients: mapClientsForForm(card.clients),
    cnts: mapCntsForForm(card.cnts)
  });
};

const moneyFromCard = (card) => ({
  syma: Number(card?.syma) || 0,
  symaPlat: Number(card?.symaPlat) || 0,
  symaDolg: Number(card?.symaDolg) || 0,
  avansOpen: Number(card?.avansOpen) || 0
});

const EMPTY_MONEY = { syma: 0, symaPlat: 0, symaDolg: 0, avansOpen: 0 };

const DocExpMoneyBar = ({ money }) => {
  const m = money || EMPTY_MONEY;
  const cells = [
    { key: "syma", label: "Начислено", value: m.syma },
    { key: "plat", label: "Оплачено", value: m.symaPlat },
    { key: "avans", label: "Неразнесённый аванс", value: m.avansOpen, warn: Math.abs(m.avansOpen) >= 0.005 },
    { key: "dolg", label: "Долг", value: m.symaDolg, warn: Math.abs(m.symaDolg) >= 0.005 }
  ];
  return (
    <div className="doc-exp-card__money">
      {cells.map((cell) => (
        <div
          key={cell.key}
          className={`doc-exp-card__money-cell${cell.warn ? " doc-exp-card__money-cell--warn" : ""}`}
        >
          <span>{cell.label}</span>
          <b>{formatMoney(cell.value)}</b>
        </div>
      ))}
    </div>
  );
};

/**
 * Карточка шапки путёвки + клиенты/товары (блоки в той же форме).
 */
const DocExpCard = ({
  idDoc,
  isNew = false,
  open,
  canEdit = true,
  reloadToken = 0,
  onSaved,
  onClose,
  onStateChange
}) => {
  const law = useSelector((state) => state.user.session?.law);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [lookups, setLookups] = useState({
    vidPutevki: [],
    skidki: [],
    vidPitania: [],
    vidLech: [],
    branches: [],
    usrStatuses: []
  });
  const [statusName, setStatusName] = useState("");
  const [headerLocked, setHeaderLocked] = useState(false);
  const [clientsLocked, setClientsLocked] = useState(false);
  const [activeTab, setActiveTab] = useState("header");
  const [placementsReloadToken, setPlacementsReloadToken] = useState(0);
  const [runtimeSettings, setRuntimeSettings] = useState(null);
  const [titleNum, setTitleNum] = useState(null);
  const [predOptions, setPredOptions] = useState([]);
  const [predLoading, setPredLoading] = useState(false);
  const [predCreateOpen, setPredCreateOpen] = useState(false);
  const [listCounts, setListCounts] = useState({ clients: 0, cnts: 0 });
  const [money, setMoney] = useState(EMPTY_MONEY);
  const [payMode, setPayMode] = useState(null);
  const [sendToDoctorClient, setSendToDoctorClient] = useState(null);
  const [cardMeta, setCardMeta] = useState(null);
  const [seats, setSeats] = useState(null);
  const predTimerRef = useRef(0);
  const predRequestRef = useRef(0);

  const clientsWatch = Form.useWatch("clients", form);
  const cntsWatch = Form.useWatch("cnts", form);
  const clientsCount = Array.isArray(clientsWatch) ? clientsWatch.length : listCounts.clients;
  const cntsCount = Array.isArray(cntsWatch) ? cntsWatch.length : listCounts.cnts;

  const submit = useCallback(() => {
    form.submit();
  }, [form]);

  const loadPreds = useCallback((query = "") => {
    clearTimeout(predTimerRef.current);
    predTimerRef.current = setTimeout(() => {
      const requestId = ++predRequestRef.current;
      setPredLoading(true);
      fetchSprPredList({ q: query, take: 50 })
        .then((data) => {
          if (requestId !== predRequestRef.current) return;
          const next = mapPredOptions(data);
          const selectedId = form.getFieldValue("idClient");
          setPredOptions((previous) => {
            const selected = previous.find((item) => item.value === selectedId);
            return selected && !next.some((item) => item.value === selectedId) ? [selected, ...next] : next;
          });
        })
        .catch(() => undefined)
        .finally(() => {
          if (requestId === predRequestRef.current) setPredLoading(false);
        });
    }, 300);
  }, [form]);

  const onPredSearch = (query) => {
    loadPreds(query);
  };

  const onPredCreated = useCallback(
    (saved) => {
      const value = toNumber(pick(saved, "idPred", "IdPred"));
      const label = pick(saved, "name", "Name") || String(value);
      if (value) {
        setPredOptions((prev) => mergePredOptions(prev, [{ value, label }]));
        form.setFieldValue("idClient", value);
      }
      setPredCreateOpen(false);
    },
    [form]
  );

  useEffect(() => {
    onStateChange?.({
      saving,
      canSave: Boolean(canEdit && !clientsLocked && !loading && runtimeSettings != null),
      titleNum,
      submit,
      activeTab,
      cardMeta
    });
  }, [
    saving,
    canEdit,
    clientsLocked,
    loading,
    runtimeSettings,
    titleNum,
    submit,
    activeTab,
    cardMeta,
    onStateChange
  ]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    loadRuntimeSettings()
      .then((settings) => {
        if (!cancelled) setRuntimeSettings(settings);
      })
      .catch(() => {
        if (!cancelled) setRuntimeSettings({});
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    fetchDocExpLookups()
      .then((data) => {
        if (!cancelled) setLookups(normalizeLookups(data));
      })
      .catch(() => {
        if (!cancelled) {
          setLookups({
            vidPutevki: [{ value: 0, label: "—" }],
            skidki: [{ value: 0, label: "—" }],
            vidPitania: [{ value: 0, label: "—" }],
            vidLech: [{ value: 0, label: "—" }],
            branches: [{ value: 0, label: "—" }],
            usrStatuses: [{ value: 0, label: "—" }]
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || runtimeSettings == null) return undefined;
    let cancelled = false;

    if (isNew) {
      setLoading(false);
      setError("");
      setStatusName("Новый");
      setTitleNum(null);
      setPredOptions([]);
      setHeaderLocked(false);
      setClientsLocked(false);
      setCardMeta(null);
      setActiveTab("header");
      setPlacementsReloadToken(0);
      setMoney(EMPTY_MONEY);
      setSeats(null);
      const empty = emptyCard();
      const autobronDays = Number(getSetting(runtimeSettings, "AUTOBRON_DAYS", 10));
      form.setFieldsValue({
        ...empty,
        docdate: toDay(empty.docdate),
        dateBeg: null,
        dateEnd: null,
        timeBeg: dayjs(DEFAULT_TIME_BEG, "HH:mm"),
        timeEnd: dayjs(DEFAULT_TIME_END, "HH:mm"),
        dateBron: dayjs().startOf("day").add(
          Number.isFinite(autobronDays) ? autobronDays : 10,
          "day"
        ),
        vidPutevki: Number(getSetting(runtimeSettings, "VID_PUT_DEFAULT", 0)) || 0,
        vidPitania: Number(getSetting(runtimeSettings, "VID_PIT_DEFAULT", 0)) || 0,
        vidPitFirstDay: Number(getSetting(runtimeSettings, "VID_PIT_FIRST_DAY", 0)) || 0,
        idBranch: getWorkplaceNumber("idBranch", 0),
        e3: Number(getSetting(runtimeSettings, "NOT_SHOW_FOOD3", 0)) !== 1,
        clients: [],
        cnts: []
      });
      setListCounts({ clients: 0, cnts: 0 });
      loadPreds("");
      return () => {
        cancelled = true;
      };
    }

    if (idDoc == null) return undefined;

    setLoading(true);
    setError("");
    fetchDocExp(idDoc)
      .then((row) => {
        if (cancelled) return;
        const card = normalizeCard(row);
        setStatusName(card.statusName || "");
        setTitleNum(card.docnum || card.idDoc || idDoc);
        setHeaderLocked(card.isClosed || card.idStatus >= 2);
        setClientsLocked(card.isClosed);
        setCardMeta(toLifecycleMeta(card));
        setActiveTab("header");
        setPlacementsReloadToken((t) => t + 1);
        setPredOptions(
          card.idClient && card.predName
            ? [{ value: card.idClient, label: card.predName }]
            : []
        );
        applyCardToForm(form, card);
        setMoney(moneyFromCard(card));
        setSeats(card.seats ?? null);
        setListCounts({
          clients: (card.clients || []).length,
          cnts: (card.cnts || []).length
        });
        loadPreds("");
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Не удалось загрузить путёвку");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      clearTimeout(predTimerRef.current);
    };
  }, [open, isNew, idDoc, form, reloadToken, runtimeSettings, loadPreds]);

  const persistCard = async (values, { successMessage } = {}) => {
    if (!canEdit || clientsLocked) return null;
    const summary = seatsSummary(values.clients, seats);
    const seatsErr = seatsErrorText(summary);
    if (seatsErr) {
      setError(seatsErr);
      message.error(seatsErr);
      return null;
    }
    setSaving(true);
    setError("");
    const clients = mapClientsForApi(values.clients);
    const cnts = mapCntsForApi(values.cnts);
    const body = {
      docdate: toApiDate(values.docdate),
      docnum: values.docnum || 0,
      idClient: values.idClient || 0,
      komment: values.komment || "",
      dateBeg: toApiDateTime(mergeDateTime(values.dateBeg, values.timeBeg)),
      dateEnd: toApiDateTime(mergeDateTime(values.dateEnd, values.timeEnd)),
      dateBron: toApiDate(values.dateBron),
      vidPutevki: values.vidPutevki || 0,
      idSkidka: values.idSkidka || 0,
      prcSkidka: values.prcSkidka ?? 0,
      prcSkidkaPit: values.prcSkidkaPit ?? 0,
      prcSkidkaLech: values.prcSkidkaLech ?? 0,
      vidPitania: values.vidPitania || 0,
      vidPitFirstDay: values.vidPitFirstDay || 0,
      e1: values.e1 ? 1 : 0,
      e2: values.e2 ? 1 : 0,
      e3:
        Number(getSetting(runtimeSettings, "NOT_SHOW_FOOD3", 0)) === 1
          ? 0
          : values.e3
            ? 1
            : 0,
      e4: values.e4 ? 1 : 0,
      idUsrStatus: values.idUsrStatus || 0,
      idBranch: values.idBranch || 0,
      idNumber: values.idNumber || 0,
      idNumberCat: values.idNumberCat || 0,
      clients,
      cnts
    };

    try {
      const saved = isNew
        ? await createDocExp(body)
        : await saveDocExp({ ...body, idDoc });
      message.success(
        successMessage || (isNew ? "Путёвка создана" : "Сохранено")
      );
      const card = normalizeCard(saved);
      setStatusName(card.statusName || "");
      setTitleNum(card.docnum || card.idDoc || idDoc);
      setHeaderLocked(card.isClosed || card.idStatus >= 2);
      setClientsLocked(card.isClosed);
      setCardMeta(toLifecycleMeta(card));
      if (card.idClient && card.predName) {
        setPredOptions((previous) =>
          mergePredOptions(previous, [{ value: card.idClient, label: card.predName }])
        );
      }
      applyCardToForm(form, card);
      setMoney(moneyFromCard(card));
      setSeats(card.seats ?? null);
      setListCounts({
        clients: (card.clients || []).length,
        cnts: (card.cnts || []).length
      });
      setPlacementsReloadToken((t) => t + 1);
      onSaved?.(saved);
      return saved;
    } catch (e) {
      setError(e.message || "Не удалось сохранить");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const onFinish = async (values) => {
    const saved = await persistCard(values);
    if (saved) onClose?.();
  };

  const onFinishFailed = ({ errorFields }) => {
    const headerNames = new Set(["docdate", "dateBeg", "dateEnd"]);
    if ((errorFields || []).some((field) => headerNames.has(field?.name?.[0]))) {
      setActiveTab("header");
    }
  };

  const onConfirmClientEdit = async (fieldName) => {
    if (!canEdit || clientsLocked) return true;
    try {
      await form.validateFields([["docdate"], ["dateBeg"], ["dateEnd"]]);
    } catch {
      return false;
    }
    const values = form.getFieldsValue(true);
    const saved = await persistCard(values, { successMessage: "Клиент сохранён" });
    return saved != null;
  };

  const onConfirmCntEdit = async (fieldName) => {
    if (!canEdit || clientsLocked) return true;
    try {
      await form.validateFields([
        ["docdate"],
        ["dateBeg"],
        ["dateEnd"],
        ["cnts", fieldName, "idTov"]
      ]);
    } catch {
      return false;
    }
    const values = form.getFieldsValue(true);
    const saved = await persistCard(values, { successMessage: "Товар сохранён" });
    return saved != null;
  };

  const headerReadOnly = !canEdit || headerLocked;
  const clientsReadOnly = !canEdit || clientsLocked;
  const cntsReadOnly = !canEdit || clientsLocked;
  const canPay = getLawFlag(law, 36) === 1 && !isNew && idDoc != null && !clientsLocked;
  const lineDebt = (cntsWatch || []).reduce((sum, c) => sum + (Number(c?.symaDolg) || 0), 0);
  const linePlat = (cntsWatch || []).reduce((sum, c) => sum + (Number(c?.symaPlat) || 0), 0);
  const canPayLines = canPay && lineDebt > 0.005;
  const canReturn = canPay && (Math.abs(money.avansOpen) > 0.005 || linePlat > 0.005);
  const periodStatusOk = isNew || cardMeta == null || cardMeta.idStatus === 0 || cardMeta.idStatus === 1;
  const periodLockReason = headerReadOnly
    ? ""
    : !periodStatusOk
      ? "Период проживания можно менять только для новой или подтверждённой путёвки"
      : linePlat > 0.005
        ? "Аванс разнесён по позициям — период проживания изменить нельзя"
        : "";
  const periodReadOnly = headerReadOnly || Boolean(periodLockReason);

  const applyPaidCard = (raw) => {
    if (!raw) {
      setPlacementsReloadToken((t) => t + 1);
      onSaved?.();
      return;
    }
    const card = normalizeCard(raw);
    setStatusName(card.statusName || "");
    setTitleNum(card.docnum || card.idDoc || idDoc);
    setHeaderLocked(card.isClosed || card.idStatus >= 2);
    setClientsLocked(card.isClosed);
    setCardMeta(toLifecycleMeta(card));
    applyCardToForm(form, card);
    setMoney(moneyFromCard(card));
    setSeats(card.seats ?? null);
    setListCounts({
      clients: (card.clients || []).length,
      cnts: (card.cnts || []).length
    });
    setPlacementsReloadToken((t) => t + 1);
    onSaved?.(raw);
    const docId = card.idDoc || idDoc;
    if (docId != null && raw.canRecalculate === undefined && raw.CanRecalculate === undefined) {
      fetchDocExpRecalculateCheck(docId)
        .then((data) => {
          const allowed = data?.allowed !== false && data?.Allowed !== false;
          setCardMeta((prev) =>
            prev
              ? {
                  ...prev,
                  canRecalculate: allowed,
                  recalculateBlockReason: data?.reason || data?.Reason || ""
                }
              : prev
          );
        })
        .catch(() => undefined);
    }
  };

  const reloadCard = useCallback(async () => {
    if (idDoc == null) return;
    try {
      const row = await fetchDocExp(idDoc);
      const card = normalizeCard(row);
      setStatusName(card.statusName || "");
      setTitleNum(card.docnum || card.idDoc || idDoc);
      setHeaderLocked(card.isClosed || card.idStatus >= 2);
      setClientsLocked(card.isClosed);
      setCardMeta(toLifecycleMeta(card));
      applyCardToForm(form, card);
      setMoney(moneyFromCard(card));
      setSeats(card.seats ?? null);
      setListCounts({
        clients: (card.clients || []).length,
        cnts: (card.cnts || []).length
      });
      setPlacementsReloadToken((t) => t + 1);
    } catch {
      /* ignore */
    }
  }, [idDoc, form]);

  const onDeleteClient = async (fieldName) => {
    if (!canEdit || clientsLocked) return false;
    const values = form.getFieldsValue(true);
    const clients = Array.isArray(values.clients) ? values.clients : [];
    const saved = await persistCard(
      { ...values, clients: clients.filter((_, index) => index !== fieldName) },
      { successMessage: "Гость удалён, размещение пересчитано" }
    );
    if (!saved) await reloadCard();
    return saved != null;
  };

  const canSendToDoctor = cardMeta?.idStatus === 2;

  const onSentToDoctor = useCallback(
    async () => {
      setSendToDoctorClient(null);
      await reloadCard();
    },
    [reloadCard]
  );

  const headerFields = (
    <div className="doc-exp-card__header">
      <Form.Item name="docnum" hidden>
        <InputNumber />
      </Form.Item>
      <div className="doc-exp-card__row">
        <Form.Item label="Статус" className="doc-exp-card__status">
          <Select
            disabled
            value={statusName || undefined}
            placeholder="Статус"
            options={statusName ? [{ value: statusName, label: statusName }] : []}
          />
        </Form.Item>
        <Form.Item
          label="Дата"
          name="docdate"
          className="doc-exp-card__docdate"
          rules={[{ required: true, message: "Укажите дату" }]}
        >
          <DatePicker format="DD.MM.YYYY" allowClear={false} disabled={headerReadOnly} />
        </Form.Item>
        <Form.Item name="dateBron" label="Бронь до" className="doc-exp-card__date-bron">
          <DatePicker format="DD.MM.YYYY" disabled={headerReadOnly} />
        </Form.Item>
      </div>
      <div className="doc-exp-card__contractor-row">
        <Form.Item
          name="idClient"
          label="Контрагент"
          className="doc-exp-card__contractor"
          getValueProps={(value) => ({ value: value || undefined })}
          getValueFromEvent={(value) => value || 0}
        >
          <Select
            allowClear
            showSearch
            placeholder="Не выбран"
            options={predOptions}
            filterOption={false}
            onSearch={onPredSearch}
            onOpenChange={(opened) => {
              if (opened) loadPreds("");
            }}
            loading={predLoading}
            disabled={headerReadOnly}
            notFoundContent={predLoading ? "Загрузка..." : "Не найдено"}
            popupRender={(menu) => (
              <>
                {menu}
                <Divider className="doc-exp-card__contractor-divider" />
                <Button
                  type="text"
                  block
                  className="doc-exp-card__contractor-add"
                  icon={<PlusOutlined />}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setPredCreateOpen(true)}
                >
                  Новый контрагент
                </Button>
              </>
            )}
          />
        </Form.Item>
        <Tooltip title="Контрагент (плательщик / организация по путёвке)">
          <UserSwitchOutlined className="doc-exp-card__contractor-icon" />
        </Tooltip>
      </div>
      <div className="doc-exp-card__dates">
        <div className="doc-exp-card__date-pair">
          <Form.Item
            name="dateBeg"
            label="Заезд"
            className="doc-exp-card__date-beg"
            tooltip={periodLockReason || undefined}
            rules={[{ required: true, message: "Укажите заезд" }]}
          >
            <DatePicker format="DD.MM.YYYY" disabled={periodReadOnly} />
          </Form.Item>
          <Form.Item name="timeBeg" className="doc-exp-card__time-beg">
            <TimePicker format="HH:mm" allowClear={false} needConfirm={false} disabled={periodReadOnly} />
          </Form.Item>
        </div>
        <div className="doc-exp-card__date-pair">
          <Form.Item
            name="dateEnd"
            label="Выезд"
            className="doc-exp-card__date-end"
            tooltip={periodLockReason || undefined}
            rules={[{ required: true, message: "Укажите выезд" }]}
          >
            <DatePicker format="DD.MM.YYYY" disabled={periodReadOnly} />
          </Form.Item>
          <Form.Item name="timeEnd" className="doc-exp-card__time-end">
            <TimePicker format="HH:mm" allowClear={false} needConfirm={false} disabled={periodReadOnly} />
          </Form.Item>
        </div>
      </div>
      <div className="doc-exp-card__row">
        <Form.Item label="Вид путёвки" name="vidPutevki">
          <Select
            options={lookups.vidPutevki}
            showSearch
            optionFilterProp="label"
            disabled={headerReadOnly}
          />
        </Form.Item>
        <Form.Item label="Питание" name="vidPitania">
          <Select
            options={lookups.vidPitania}
            showSearch
            optionFilterProp="label"
            disabled={headerReadOnly}
            onChange={(value) => {
              const item = lookups.vidPitania.find((o) => o.value === value);
              if (item && value > 0) form.setFieldsValue(mealFlagsFromRazmer(item.razmer));
            }}
          />
        </Form.Item>
      </div>
      <div className="doc-exp-card__row">
        <Form.Item label="Скидка" name="idSkidka">
          <Select
            options={lookups.skidki}
            showSearch
            optionFilterProp="label"
            disabled={headerReadOnly}
          />
        </Form.Item>
        <Form.Item label="% скидки" name="prcSkidka">
          <InputNumber min={0} max={100} disabled={headerReadOnly} />
        </Form.Item>
      </div>
      <div className="doc-exp-card__row">
        <Form.Item label="Питание в день заезд." name="vidPitFirstDay">
          <Select
            options={[
              { value: 0, label: "Не выбрано" },
              { value: 1, label: "Завтрак" },
              { value: 2, label: "Обед" },
              { value: 3, label: "Полдник" },
              { value: 4, label: "Ужин" }
            ]}
            disabled={headerReadOnly}
          />
        </Form.Item>
        <Form.Item label="Приёмы пищи">
          <div className="doc-exp-card__meals">
            <MealCheck name="e1" title="Завтрак" disabled={headerReadOnly} src={mealBreakfastSrc} />
            <MealCheck name="e2" title="Обед" disabled={headerReadOnly} src={mealLunchSrc} />
            {Number(getSetting(runtimeSettings, "NOT_SHOW_FOOD3", 0)) !== 1 ? (
              <MealCheck name="e3" title="Полдник" disabled={headerReadOnly} src={mealSnackSrc} />
            ) : null}
            <MealCheck name="e4" title="Ужин" disabled={headerReadOnly} src={mealDinnerSrc} />
          </div>
        </Form.Item>
      </div>
      <div className="doc-exp-card__row">
        <Form.Item label="Статус польз." name="idUsrStatus">
          <Select
            options={lookups.usrStatuses}
            showSearch
            optionFilterProp="label"
            disabled={headerReadOnly}
          />
        </Form.Item>
        <Form.Item label="Филиал" name="idBranch">
          <Select
            options={lookups.branches}
            showSearch
            optionFilterProp="label"
            disabled={headerReadOnly}
          />
        </Form.Item>
      </div>
      <Form.Item name="komment" className="doc-exp-card__comment">
        <Input.TextArea rows={2} placeholder="Комментарий" disabled={headerReadOnly} />
      </Form.Item>
    </div>
  );

  const listResetKey = `${isNew ? "new" : idDoc ?? ""}:${open ? 1 : 0}`;

  const clientsFields = (
    <DocExpClientsPanel
      clientsWatch={clientsWatch || []}
      clientsReadOnly={clientsReadOnly}
      formItemLayout={formItemLayout}
      lookups={lookups}
      resetKey={listResetKey}
      saving={saving}
      onConfirmEdit={onConfirmClientEdit}
      defaultVidLech={Number(getSetting(runtimeSettings, "VID_LECH_DEFAULT", 0)) || 0}
      idDoc={isNew ? null : idDoc}
      canEditDailyMeals={getLawFlag(law, 45) === 1}
      mealTypeOptions={lookups.vidPitania}
      onDailyMealsSaved={() => {
        setPlacementsReloadToken((t) => t + 1);
        onSaved?.();
      }}
      canSendToDoctor={canSendToDoctor}
      onSendToDoctor={setSendToDoctorClient}
      seats={seats}
      numberName={cardMeta?.numberName}
      catName={cardMeta?.catName}
      onDeleteClient={onDeleteClient}
      onReload={reloadCard}
    />
  );

  const cntsFields = (
    <DocExpCntsPanel
      form={form}
      cntsWatch={cntsWatch || []}
      cntsReadOnly={cntsReadOnly}
      formItemLayout={formItemLayout}
      resetKey={listResetKey}
      saving={saving}
      onConfirmEdit={onConfirmCntEdit}
    />
  );

  const placementsFields = (
    <DocExpPlacementsPanel
      idDoc={isNew ? null : idDoc}
      active={activeTab === "placements"}
      reloadToken={placementsReloadToken}
    />
  );

  const paymentsFields = (
    <div className="doc-exp-card__payments-tab">
      {canPay ? (
        <Space wrap className="doc-exp-card__pay-actions">
          <Button onClick={() => setPayMode("advance")}>Принять аванс</Button>
          <Button disabled={!canPayLines} onClick={() => setPayMode("pay")}>
            Оплатить
          </Button>
          <Button disabled={!canReturn} onClick={() => setPayMode("return")}>
            Возврат
          </Button>
        </Space>
      ) : null}
      <DocExpPaymentsPanel
        idDoc={isNew ? null : idDoc}
        active={activeTab === "payments"}
        reloadToken={placementsReloadToken}
      />
    </div>
  );

  return (
    <div className="doc-exp-card">
      {error ? <Alert className="doc-exp-card__error" type="error" showIcon message={error} /> : null}
      {loading || runtimeSettings == null ? (
        <div className="doc-exp-card__center">
          <Spin />
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          colon={false}
          requiredMark={requiredMark}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          className="doc-exp-card__form"
        >
          {!isNew ? <DocExpMoneyBar money={money} /> : null}
          <Tabs
            className="doc-exp-card__tabs"
            activeKey={activeTab}
            onChange={setActiveTab}
            destroyOnHidden={false}
            items={[
              { key: "header", label: "Основные", children: headerFields },
              {
                key: "clients",
                label: tabLabel("Гости", clientsCount),
                forceRender: true,
                children: clientsFields
              },
              {
                key: "cnts",
                label: tabLabel("Товары", cntsCount),
                forceRender: true,
                children: cntsFields
              },
              {
                key: "placements",
                label: "Размещения",
                children: placementsFields
              },
              {
                key: "payments",
                label: "Оплаты",
                children: paymentsFields
              }
            ]}
          />
        </Form>
      )}
      <DocExpPayDrawer
        open={Boolean(payMode)}
        mode={payMode}
        idDoc={idDoc}
        onClose={() => setPayMode(null)}
        onDone={(doc) => {
          setPayMode(null);
          applyPaidCard(doc);
        }}
      />
      <SprPred
        open={predCreateOpen}
        idPred={null}
        isNew
        onClose={() => setPredCreateOpen(false)}
        onSaved={onPredCreated}
      />
      <DocExpSendToDoctorModal
        open={Boolean(sendToDoctorClient)}
        idDoc={idDoc}
        client={sendToDoctorClient}
        dateBeg={form.getFieldValue("dateBeg")}
        dateEnd={form.getFieldValue("dateEnd")}
        onClose={() => setSendToDoctorClient(null)}
        onSent={onSentToDoctor}
      />
    </div>
  );
};

export default DocExpCard;
